'use client'

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'

import { cn } from '../lib/cn'

export type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  orientation?: 'vertical' | 'horizontal'
  wrapperClassName?: string
  autoPadding?: boolean
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Viewport>, ScrollAreaProps>(
  ({ className, children, orientation = 'vertical', wrapperClassName, autoPadding = false, ...props }, ref) => {
    const internalRef = React.useRef<HTMLDivElement>(null)
    const [hasScrollbar, setHasScrollbar] = React.useState(false)

    React.useImperativeHandle(ref, () => internalRef.current!)

    React.useEffect(() => {
      if (!autoPadding) return

      const viewport = internalRef.current
      if (!viewport) return

      const checkOverflow = () => {
        setHasScrollbar(viewport.scrollHeight > viewport.clientHeight)
      }

      const observer = new ResizeObserver(checkOverflow)
      observer.observe(viewport)
      checkOverflow()

      return () => observer.disconnect()
    }, [autoPadding, children])

    return (
      <ScrollAreaPrimitive.Root data-slot='scroll-area' className={cn('relative', wrapperClassName)} {...props}>
        <ScrollAreaPrimitive.Viewport
          ref={internalRef}
          data-slot='scroll-area-viewport'
          className={cn(
            'focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1',
            className,
            autoPadding && hasScrollbar && 'pr-2.5',
          )}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>

        <ScrollBar orientation={orientation} className='w-2 [&_div]:bg-border'>
          <ScrollAreaPrimitive.Thumb />
        </ScrollBar>

        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot='scroll-area-scrollbar'
      orientation={orientation}
      className={cn(
        'flex touch-none p-px transition-colors select-none',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot='scroll-area-thumb'
        className='bg-border relative flex-1 rounded-full'
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea }
